import { TextInput } from '@mantine/core';
import { GridApi, IFilterParams, IFloatingFilterParams } from 'ag-grid-community';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { get, isEqual, noop } from 'lodash';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

export const HR_TERM_ID_FILTER_KEY = 'termId' as const;

export interface HrIdSetFilterModel {
  filterType: 'idSet';
  type: 'belongsTo';
  filter: string;
}

/** Converts comma, semicolon, or whitespace-delimited input into unique positive IDs. */
export function normalizeHrTermIds(value: unknown): number[] {
  const values = Array.isArray(value) ? value : [value];
  const ids: number[] = [];
  const seen = new Set<number>();

  values
    .flatMap((item) => (typeof item === 'number' || typeof item === 'string' ? String(item).split(/[,;\s]+/) : []))
    .forEach((item) => {
      const id = Number(item);
      if (Number.isSafeInteger(id) && id > 0 && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });

  return ids;
}

/** Returns the canonical comma-separated value used for query-driven initial filters. */
export function canonicalHrTermIds(value: unknown): string {
  return normalizeHrTermIds(value).join(',');
}

/** AG Grid parent filter implementing ID-set membership. */
export const HrIdSetColumnFilter = forwardRef(({ filterChangedCallback, colDef }: IFilterParams, ref) => {
  const value = useRef('');

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: Record<string, any> }) {
      const ids = normalizeHrTermIds(value.current);
      if (ids.length === 0) return true;

      return ids.includes(Number(get(data, colDef.field ?? HR_TERM_ID_FILTER_KEY)));
    },

    getModel(): HrIdSetFilterModel | null {
      const filter = canonicalHrTermIds(value.current);
      return filter ? { filterType: 'idSet', type: 'belongsTo', filter: value.current } : null;
    },

    setModel(model: HrIdSetFilterModel | null) {
      value.current = model?.filter ?? '';
    },

    isFilterActive() {
      return normalizeHrTermIds(value.current).length > 0;
    },

    onFloatingFilterChanged(nextValue: string) {
      if (!isEqual(value.current, nextValue)) {
        value.current = nextValue;
        filterChangedCallback();
      }
    },
  }));

  return <></>;
});

interface HrIdSetFloatingFilterProps extends IFloatingFilterParams {
  onChange?: FilterChangeCallback;
}

interface HrIdSetFloatingFilterHandle {
  onParentModelChanged: () => void;
}

/** Floating ID-set editor; a row matches when its ID belongs to any entered value. */
export const HrIdSetFloatingFilter = forwardRef<HrIdSetFloatingFilterHandle, HrIdSetFloatingFilterProps>(
  function HrIdSetFloatingFilter({ api, onChange, parentFilterInstance = noop }, ref) {
    const [value, setValue] = useState(() => api?.getFilterModel()?.[HR_TERM_ID_FILTER_KEY]?.filter ?? '');

    useImperativeHandle(ref, () => ({ onParentModelChanged: noop }));

    const pushModel = useCallback(
      (nextValue: string) => {
        const filter = canonicalHrTermIds(nextValue);
        const model: HrIdSetFilterModel | null = filter
          ? { filterType: 'idSet', type: 'belongsTo', filter: nextValue }
          : null;

        if (onChange) {
          onChange({ [HR_TERM_ID_FILTER_KEY]: model });
          return;
        }

        parentFilterInstance((instance: any) => instance.onFloatingFilterChanged(nextValue));
      },
      [onChange, parentFilterInstance]
    );

    useEffect(() => {
      /** Keeps the editor synchronized with navigation/profile model changes. */
      const handleModelUpdated = ({ api: updatedApi }: { api: GridApi }) => {
        setValue(updatedApi.getFilterModel()[HR_TERM_ID_FILTER_KEY]?.filter ?? '');
      };

      api.addEventListener('modelUpdated', handleModelUpdated);
      return () => api.removeEventListener('modelUpdated', handleModelUpdated);
    }, [api]);

    return (
      <TextInput
        size="sm"
        w="100%"
        aria-label="ID patrí do"
        placeholder="ID patrí do"
        value={value}
        onChange={(event) => {
          const nextValue = event.currentTarget.value;
          setValue(nextValue);
          pushModel(nextValue);
        }}
      />
    );
  }
);
